import { isValidCountryCode } from 'src/utils/country';
import {
  BuyerWithSalesDto,
  CreateRetailSaleWithPaymentInput,
  EventWithData,
  ExternalPaymentMethodType,
  ListingDetails,
  PointOfSaleSaleStatus,
  TicketType,
} from 'src/WebApiController';

import { RetailSaleFormValues } from './types';

/**
 * Extended input type that includes external payment fields not yet in the
 * auto-generated CreateRetailSaleWithPaymentInput.
 * TODO: Remove this extension once Services 29.20.1 publishes and types are regenerated.
 */
type CreateRetailSaleWithExternalPaymentInput =
  CreateRetailSaleWithPaymentInput & {
    externalPaymentMethodType?: ExternalPaymentMethodType | null;
    externalPaymentNote?: string | null;
    externalPaymentAmount?: number | null;
    externalPaymentDate?: string | null;
  };

export const getRetailExternalSaleId = (listingId: number) => {
  return `Retail-${listingId}-${Date.now()}`;
};

export const getRetailMerchantReferenceId = (saleId: number) => {
  return `Retail-${saleId}-${Date.now()}`;
};

export const buildRetailSaleInput = (
  values: RetailSaleFormValues,
  listing: ListingDetails | undefined,
  selectedBuyer: BuyerWithSalesDto | undefined,
  event: EventWithData | undefined,
  /**
   * When true (default), external payment fields are included in the input
   * (Step 1 full-payment path, or non-external payments).
   * When false, external fields are omitted so the sale is created as Unpaid
   * (Step 2 partial external path where ApplyExternalPayment is called separately).
   */
  includeExternalFields = true
): CreateRetailSaleWithExternalPaymentInput => {
  const isExternal = values.paymentMethodChoice === 'external';
  const externalFields =
    isExternal && includeExternalFields
      ? {
          externalPaymentMethodType: values.externalPaymentMethodType ?? null,
          externalPaymentNote: values.externalPaymentNote ?? null,
          externalPaymentAmount: values.externalPaymentAmount ?? null,
          externalPaymentDate: values.externalPaymentDate ?? null,
        }
      : {
          externalPaymentMethodType: null,
          externalPaymentNote: null,
          externalPaymentAmount: null,
          externalPaymentDate: null,
        };

  return {
    listingId: values.listingId,
    posEventId: listing?.posEvId ?? '',
    viagogoEventId: listing?.viagEvId ?? null,
    eventMappingId: listing?.viagEvMapId ?? null,
    venueId: event?.event?.venId ?? null,
    saleDate: values.saleDate ?? null,
    currencyCode: values.currencyCode ?? null,
    totalNetProceeds: values.totalNetProceeds ?? null,
    inHandDate: values.inHandDate ?? null,
    quantitySold: values.quantitySold ?? null,
    section: values.section ?? null,
    row: values.row ?? null,
    seatFrom: values.seatFrom ?? null,
    seatTo: values.seatTo ?? null,
    ticketType: (values.ticketType as TicketType) ?? null,
    buyerName: selectedBuyer?.buyerName ?? null,
    buyerEmail: selectedBuyer?.buyerEmail ?? null,
    buyerPhone: selectedBuyer?.buyerPhoneNumber ?? null,
    internalNotes: values.privateNotes ?? null,
    marketplaceSaleId: values.externalSaleId ?? null,
    saleStatus: PointOfSaleSaleStatus.PendingFulfillment,
    lineItems:
      values.marketplacePaymentLines?.map((line) => ({
        paymentLineType: line.paymentLineType,
        amount: line.amount,
        ratePercent: line.ratePercent ?? null,
        reference: line.externalPaymentId || null,
        description: line.description || null,
        saleLineItemChargeReason: line.saleLineItemChargeReason ?? null,
        saleLineItemCreditReason: line.saleLineItemCreditReason ?? null,
      })) ?? null,
    ...externalFields,
  };
};

export const getPaymentAmount = (values: RetailSaleFormValues) => {
  const { totalNetProceeds, marketplacePaymentLines } = values;
  const marketplaceLinesCost = (marketplacePaymentLines ?? []).reduce(
    (ret, line) => {
      ret += line.amount ?? 0;
      return ret;
    },
    0
  );
  return (totalNetProceeds ?? 0) + marketplaceLinesCost;
};

export const getPaymentCountryCode = (
  selectedBuyer: BuyerWithSalesDto | undefined,
  knownCountries?: Record<string, string> | null
) => {
  const nameToCountryCode = Object.entries(knownCountries ?? {}).reduce(
    (acc, [code, name]) => {
      acc[name] = code;
      return acc;
    },
    {} as Record<string, string>
  );
  const contryName =
    selectedBuyer?.billingCountry ?? selectedBuyer?.shippingCountry;
  if (contryName) {
    // incase the country names are set as code
    if (isValidCountryCode(contryName)) {
      return contryName;
    }
    if (nameToCountryCode[contryName]) {
      const countryCode = nameToCountryCode[contryName];
      if (isValidCountryCode(countryCode)) {
        return countryCode;
      }
    }
  }

  return 'US';
};
